/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Dropdown, DropdownSkeleton } from 'carbon-components-react';
import _ from 'lodash';
import React, { useState, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { capitalizeFirstLetter } from '../../../../ia/util/string';
import {
  getTTSModelLanguageWithCountryDescription,
  getVoiceName,
  isModelExpressive
} from '../../../helpers/text-helpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { sharedMessages as messages } from '../../messages';

import './ControlledDropdownSelector.scss';

interface ControlledDropdownVoiceSelectorProps {
  field: string;
  id: string;
  items: Array<any>;
  onChange: Function;
  displayProp?: string;
  itemProp?: string;
  enableNone?: Boolean;
  labelText?: string;
  titleText: string;
  helperText?: string;
  instanceLoaded: Boolean;
  selectedLanguage?: string;
}

const ControlledDropdownVoiceSelector = (
  props: ControlledDropdownVoiceSelectorProps
) => {
  const {
    id,
    displayProp,
    field,
    itemProp,
    items,
    selectedLanguage,
    titleText,
    helperText,
    labelText,
    enableNone,
    onChange,
    instanceLoaded
  } = props;

  const [selectedValue, setSelectedValue] = useState();
  const [index, setIndex] = useState(0);

  // We copy list because we might modify it.
  const [itemList, setItemList] = useState([]);

  const intl = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue } = useFormHelpers(formMethods);

  const NONE = intl.formatMessage(messages.none);

  // Find the default Item
  const findDefaultItem = useCallback(
    (defaultItem, list) => {
      if (defaultItem && list.length > 0) {
        let indexDefaultValue: number;
        if (selectedLanguage) {
          indexDefaultValue = list.findIndex((item: any) => {
            return item?.name === defaultItem;
          });
        } else {
          indexDefaultValue = list.findIndex(
            (item: any) =>
              getTTSModelLanguageWithCountryDescription(item?.language) ===
              getTTSModelLanguageWithCountryDescription(defaultItem)
          );
        }
        return indexDefaultValue !== -1 ? indexDefaultValue : 0;
      }
    },
    [selectedLanguage]
  );

  // Add None to Item List
  const updateItemList = useCallback(() => {
    let modifiedList = [];
    const trackCountryItems = [];
    if (items && items.length > 0) {
      if (!selectedLanguage) {
        items.forEach(item => {
          const itemCountry = getTTSModelLanguageWithCountryDescription(
            item.language
          );
          if (!trackCountryItems.includes(itemCountry)) {
            modifiedList.push(item);
            trackCountryItems.push(itemCountry);
          }
        });
        modifiedList.sort((a, b) =>
          getTTSModelLanguageWithCountryDescription(a.language) >
          getTTSModelLanguageWithCountryDescription(b.language)
            ? 1
            : -1
        );
      } else {
        modifiedList = items.filter(
          item =>
            getTTSModelLanguageWithCountryDescription(item.language) ===
            getTTSModelLanguageWithCountryDescription(selectedLanguage)
        );
      }

      if (enableNone) {
        // Add a None option to the top of Items if enabled.
        modifiedList.unshift({
          [displayProp]: NONE,
          [itemProp]: null
        });
      }
      const value = getFieldValue(field);
      const defaultItemIndex = findDefaultItem(value, modifiedList);
      setIndex(prevIndex =>
        prevIndex !== defaultItemIndex ? defaultItemIndex : prevIndex
      );
      setSelectedValue(itemList[defaultItemIndex]);
    }
    return modifiedList;
  }, [
    NONE,
    displayProp,
    enableNone,
    field,
    findDefaultItem,
    getFieldValue,
    itemList,
    itemProp,
    items,
    selectedLanguage
  ]);

  // When Field & Items change, update everything.
  useEffect(() => {
    const list = updateItemList();
    setItemList(prevList => (_.isEqual(prevList, list) ? prevList : list));
  }, [index, items, onChange, selectedLanguage, updateItemList]);

  const voiceToString = useCallback(
    item => {
      if (selectedLanguage) {
        const name = getVoiceName(item?.friendly_name);
        const gender = capitalizeFirstLetter(item?.gender);
        const expressive = isModelExpressive(item?.friendly_name)
          ? 'Expressive'
          : '';

        if (expressive) {
          return `${name} (${gender}) - ${expressive}`;
        }
        return `${name} (${gender})`;
      }
      return getTTSModelLanguageWithCountryDescription(item?.language);
    },
    [selectedLanguage]
  );

  return (
    <>
      {instanceLoaded &&
      itemList &&
      itemList.length > 0 &&
      typeof index !== 'undefined' ? (
        // If field, use a Controlled version.
        <Dropdown
          id={id}
          direction={'top'}
          items={itemList}
          titleText={titleText}
          label={labelText}
          helperText={helperText}
          initialSelectedItem={itemList[index]}
          selectedItem={selectedValue}
          itemToString={voiceToString}
          onChange={({ selectedItem }) => {
            setSelectedValue(selectedItem);
            onChange(selectedItem);
          }}
        />
      ) : (
        <DropdownSkeleton />
      )}
      <div className="TabContent__spacer32" />
    </>
  );
};

export { ControlledDropdownVoiceSelector };
